/**
 * Two `sx` fragments for controls that must line up with something the browser does not measure the
 * way the eye does. Both cancel a rule the house theme creates.
 *
 * ⚠⚠ A MARGIN ON A DIRECT CHILD OF A `spacing` STACK IS DEAD CSS, and that is why `besideField` is
 * a fragment with a rule attached rather than a number to drop anywhere. MUI's Stack emits
 * `.MuiStack-root > :not(style):not(style) { margin: 0 }` whenever `spacing` is set, and that
 * selector outranks the child's own `sx` class, so the margin never applies and nothing reports it.
 * Measured 2026-09-02: `CreditBlock`'s three `mt: 0.5` buttons had been inert since they were
 * written. Use `besideField` only on a Stack carrying `useFlexGap` (which spaces with `gap` and
 * resets nothing), or on a row with no `spacing` at all.
 */

/**
 * How far a labelled TextField's INPUT BOX sits below the top of its own field, in spacing units.
 *
 * ⚠⚠ THE LABEL IS OUTSIDE THE FIELD IN THIS THEME AND IS STILL PART OF THE FIELD'S BOX.
 * `theme.tsx` hides the outlined notch (`legend: { display: 'none' }`), pulls the outline up
 * (`fieldset: { top: 0 }`) and pushes the input down by `theme.spacing(3)` — 24px — so the label
 * prints above the box in the house style. That 24px is a `margin-top` on the input, so the
 * `MuiFormControl` is 24px taller than the box you see, ALL OF IT ABOVE the box.
 */
export const BESIDE_FIELD_MT = 3

/**
 * A control that sits BESIDE a labelled TextField in a `flex-start` row and must line up with the
 * INPUT BOX rather than with the top of the label.
 *
 * Without it, `alignItems: 'center'` centres the sibling on label + box and it rides 12px high;
 * measured on the spend-cap row, the button's centre was 67.4 against the input's 79.4.
 *
 * ⚠ `alignItems: 'flex-end'` is the simpler fix and needs nothing from this file, but ONLY for a
 * field with no helper text. With one, the bottom of the field is the helper line and the button
 * drops below the input.
 *
 * ⚠ In a stack that becomes a COLUMN at small widths the offset has to go with the row —
 * `mt: { xs: <the column's own gap>, sm: BESIDE_FIELD_MT }` — or the wrapped button gains 24px of
 * space above it that means nothing.
 */
export const besideField = { mt: BESIDE_FIELD_MT } as const

/**
 * `besideField` for a row that becomes a COLUMN at small widths, where the Stack's own gap already
 * separates the wrapped control and the 24px would be space that means nothing.
 *
 * ⚠ It assumes the column spaces itself — a `useFlexGap` Stack, or one with `spacing`. A column
 * that does neither needs its own `xs` value rather than this.
 */
export const besideFieldRow = { mt: { xs: 0, sm: BESIDE_FIELD_MT } } as const

/**
 * A text Button whose LABEL must sit on the container's right edge, beside labels that are not
 * buttons.
 *
 * ⚠ A text Button carries `theme.spacing(0.8, 2)` of padding (`theme.tsx`, `MuiButton`), so its BOX
 * ends on the edge while its TEXT ends 16px short of it. Beside a plain `Link`, which has no
 * padding, the column reads ragged: measured, the buttons' labels ended at x=672 and the invoice
 * "View" link at x=688. Pulling each button out by its own padding puts all three on 688.
 *
 * The house theme already does this for `MuiListItemButton` (`marginLeft`/`marginRight` of
 * `theme.spacing(-2)`), which is the precedent rather than an invention here.
 *
 * ⚠ The button's hover surface then reaches 16px past the container's edge, into the panel's
 * padding. That is the trade: the ink lines up, the hit area overhangs. Not for a container that
 * clips its overflow.
 */
export const flushRight = { mr: -2 } as const

/** `flushRight`'s mirror, for a text Button on the container's LEFT edge. */
export const flushLeft = { ml: -2 } as const

/**
 * An input with NO label (a bare Select, most often) in a row beside text, buttons or other small
 * fields.
 *
 * ⚠⚠ The theme's 24px `marginTop` (`BESIDE_FIELD_MT`) is on EVERY `MuiInputBase`, not only on a
 * labelled TextField, so a bare Select carries 24px of empty space above its box and rides 12px
 * LOW in a centred row. Measured on the Members page: each role Select's centre was 186.5 against
 * 174.5 for the delete button beside it, and the support grant's Select 12px below its button.
 *
 * ⚠ It is invisible where the input is a DIRECT child of a Stack with `spacing`, because that
 * Stack's `margin: 0` reset (see the top of this file) removes the 24px already — the Usage
 * page's period Select is one. Wrap the input in a Tooltip or a Box, or move it to a `gap` row,
 * and the 24px comes back. `bareInput` is right in both places.
 */
export const bareInput = { mt: 0 } as const
